/**
 * Access section: visibility, gender, age, categories.
 */

import { type ReactNode } from "react";
import { IconCheck } from "../../../../../components/Icons.tsx";
import {
  InlinePickerRow,
  ExpandablePicker,
  InlineStepper,
  InlineToggle,
} from "../InlineFields.tsx";
import { SmartSection } from "../SmartSection.tsx";
import type { EventVisibility, Gender } from "../../types.ts";
import type { Category } from "../../types.ts";

export interface AccessSectionProps {
  id: string;
  visibility: EventVisibility;
  setVisibility: (v: EventVisibility) => void;
  visibilityOptions: Array<{
    value: EventVisibility;
    label: string;
    description?: string;
  }>;
  showVisibilityPicker: boolean;
  setShowVisibilityPicker: (v: boolean) => void;
  genderRestriction: Gender;
  setGenderRestriction: (v: Gender) => void;
  genderOptions: Array<{ value: Gender; label: string }>;
  showGenderPicker: boolean;
  setShowGenderPicker: (v: boolean) => void;
  minAge: number | null;
  setMinAge: (v: number | null) => void;
  maxAge: number | null;
  setMaxAge: (v: number | null) => void;
  featured: boolean;
  setFeatured: (v: boolean) => void;
  selectedCategoryIds: string[];
  toggleCategory: (id: string) => void;
  allCategories: Category[];
  icon: ReactNode;
  summary: string;
  filledCount: number;
  totalCount: number;
  hint?: string;
}

export function AccessSection({
  id,
  visibility,
  setVisibility,
  visibilityOptions,
  showVisibilityPicker,
  setShowVisibilityPicker,
  genderRestriction,
  setGenderRestriction,
  genderOptions,
  showGenderPicker,
  setShowGenderPicker,
  minAge,
  setMinAge,
  maxAge,
  setMaxAge,
  featured,
  setFeatured,
  selectedCategoryIds,
  toggleCategory,
  allCategories,
  icon,
  summary,
  filledCount,
  totalCount,
  hint,
}: AccessSectionProps) {
  return (
    <SmartSection
      id={id}
      title="Доступ"
      icon={icon}
      summary={summary}
      filledCount={filledCount}
      totalCount={totalCount}
      hint={hint}
      defaultOpen={false}
    >
      <div className="card overflow-hidden mb-4">
        <InlinePickerRow
          label="Видимость"
          value={
            visibilityOptions.find((o) => o.value === visibility)?.label ?? ""
          }
          onClick={() => setShowVisibilityPicker(!showVisibilityPicker)}
          isOpen={showVisibilityPicker}
        />
        <ExpandablePicker
          isOpen={showVisibilityPicker}
          options={visibilityOptions}
          value={visibility}
          onChange={setVisibility}
          onClose={() => setShowVisibilityPicker(false)}
        />
        <InlinePickerRow
          label="Пол"
          value={
            genderOptions.find((o) => o.value === genderRestriction)?.label ??
            ""
          }
          onClick={() => setShowGenderPicker(!showGenderPicker)}
          isOpen={showGenderPicker}
        />
        <ExpandablePicker
          isOpen={showGenderPicker}
          options={genderOptions}
          value={genderRestriction}
          onChange={setGenderRestriction}
          onClose={() => setShowGenderPicker(false)}
        />
        <InlineStepper
          label="Мин. возраст"
          value={minAge}
          onChange={setMinAge}
          min={0}
          max={120}
          step={1}
          placeholder="Нет"
        />
        <InlineStepper
          label="Макс. возраст"
          value={maxAge}
          onChange={setMaxAge}
          min={0}
          max={120}
          step={1}
          placeholder="Нет"
        />
        <InlineToggle
          label="В анонсы"
          description="Показывать в карусели на главной"
          checked={featured}
          onChange={setFeatured}
          color="amber"
        />
      </div>
      <div>
        <p className="text-[13px] text-slate-500 mb-2">Категории</p>
        <div className="flex flex-wrap gap-2">
          {allCategories.map((cat) => {
            const isSelected = selectedCategoryIds.includes(cat.id);
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleCategory(cat.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                  isSelected
                    ? "ring-2 ring-offset-1"
                    : "opacity-70 active:opacity-100"
                }`}
                style={{
                  backgroundColor: cat.color ? `${cat.color}20` : "#f1f5f9",
                  color: cat.color || "#64748b",
                  ...(isSelected && cat.color ? { ringColor: cat.color } : {}),
                  ...(isSelected && !cat.color ? { ringColor: "#64748b" } : {}),
                }}
              >
                {isSelected && <IconCheck className="w-3.5 h-3.5" />}
                {cat.name}
              </button>
            );
          })}
          {allCategories.length === 0 && (
            <p className="text-[13px] text-slate-500">
              Нет доступных категорий
            </p>
          )}
        </div>
      </div>
    </SmartSection>
  );
}
